import React, { useState } from 'react';
import { 
  BarChart3, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Eye,
  MousePointer,
  ShoppingCart,
  Plus,
  Edit,
  Trash2,
  Play,
  Pause
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Campaign } from '../types';
import { GlobalAdminDashboard } from './GlobalAdminDashboard';
import { PartnerAdminDashboard } from './PartnerAdminDashboard';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // Show Global Admin Dashboard for global admins
  if (user?.role === 'global_admin') {
    return <GlobalAdminDashboard />;
  }

  // Show Partner Admin Dashboard for partner admins
  if (user?.role === 'partner_admin') {
    return <PartnerAdminDashboard />;
  }

  const [campaigns, setCampaigns] = useState<Campaign[]>([
    {
      id: '1',
      name: 'Summer Sale 2024',
      description: 'Promotional campaign for summer products',
      status: 'active',
      budget: 10000,
      spent: 7500,
      impressions: 125000,
      clicks: 2500,
      conversions: 180,
      created_by: '1',
      start_date: '2024-01-15',
      end_date: '2024-02-15',
      created_at: '2024-01-10',
    },
    {
      id: '2',
      name: 'Black Friday Special',
      description: 'Holiday season marketing push',
      status: 'completed',
      budget: 25000,
      spent: 24800,
      impressions: 350000,
      clicks: 8900,
      conversions: 520,
      created_by: '2',
      partner_id: '1',
      start_date: '2023-11-20',
      end_date: '2023-11-30',
      created_at: '2023-11-15',
    },
    {
      id: '3',
      name: 'Product Launch',
      description: 'New product awareness campaign',
      status: 'draft',
      budget: 15000,
      spent: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      created_by: '1',
      start_date: '2024-02-01',
      end_date: '2024-03-01',
      created_at: '2024-01-25',
    },
  ]);

  const [showCreateModal, setShowCreateModal] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMetrics = () => {
    const totalCampaigns = campaigns.length;
    const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
    const totalBudget = campaigns.reduce((sum, c) => sum + c.budget, 0);
    const totalSpent = campaigns.reduce((sum, c) => sum + c.spent, 0);
    const totalImpressions = campaigns.reduce((sum, c) => sum + c.impressions, 0);
    const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0);
    const totalConversions = campaigns.reduce((sum, c) => sum + c.conversions, 0);

    return {
      totalCampaigns,
      activeCampaigns,
      totalBudget,
      totalSpent,
      totalImpressions,
      totalClicks,
      totalConversions,
      ctr: totalImpressions > 0 ? (totalClicks / totalImpressions * 100).toFixed(2) : '0',
      conversionRate: totalClicks > 0 ? (totalConversions / totalClicks * 100).toFixed(2) : '0',
    };
  };

  const metrics = getMetrics();

  const toggleCampaignStatus = (id: string) => {
    setCampaigns(prev => prev.map(campaign => {
      if (campaign.id === id) {
        const newStatus = campaign.status === 'active' ? 'paused' : 'active';
        return { ...campaign, status: newStatus };
      }
      return campaign;
    }));
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    change?: string;
  }> = ({ title, value, icon, color, change }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          {change && (
            <p className="text-sm text-green-600 mt-1">
              ↗ {change}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {user?.role === 'global_admin' ? 'Global Dashboard' : 
             user?.role === 'partner_admin' ? 'Partner Dashboard' : 
             'Campaign Overview'}
          </h1>
          <p className="text-gray-600 mt-2">
            Monitor and manage your marketing campaigns
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Campaigns"
            value={metrics.totalCampaigns}
            icon={<BarChart3 className="text-white" size={24} />}
            color="bg-blue-500"
          />
          <MetricCard
            title="Active Campaigns"
            value={metrics.activeCampaigns}
            icon={<TrendingUp className="text-white" size={24} />}
            color="bg-green-500"
          />
          <MetricCard
            title="Total Budget"
            value={`$${metrics.totalBudget.toLocaleString()}`}
            icon={<DollarSign className="text-white" size={24} />}
            color="bg-purple-500"
          />
          <MetricCard
            title="Total Spent"
            value={`$${metrics.totalSpent.toLocaleString()}`}
            icon={<DollarSign className="text-white" size={24} />}
            color="bg-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <MetricCard
            title="Total Impressions"
            value={metrics.totalImpressions.toLocaleString()}
            icon={<Eye className="text-white" size={24} />}
            color="bg-indigo-500"
          />
          <MetricCard
            title="Total Clicks"
            value={metrics.totalClicks.toLocaleString()}
            icon={<MousePointer className="text-white" size={24} />}
            color="bg-cyan-500"
          />
          <MetricCard
            title="Total Conversions"
            value={metrics.totalConversions.toLocaleString()}
            icon={<ShoppingCart className="text-white" size={24} />}
            color="bg-emerald-500"
          />
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <MetricCard
            title="Click Through Rate"
            value={`${metrics.ctr}%`}
            icon={<TrendingUp className="text-white" size={24} />}
            color="bg-pink-500"
          />
          <MetricCard
            title="Conversion Rate"
            value={`${metrics.conversionRate}%`}
            icon={<BarChart3 className="text-white" size={24} />}
            color="bg-teal-500"
          />
        </div>

        {/* Campaigns Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Campaigns</h2>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 sm:mt-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center"
            >
              <Plus size={20} className="mr-2" />
              New Campaign
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget/Spent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                        <div className="text-sm text-gray-500">{campaign.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>${campaign.spent.toLocaleString()} / ${campaign.budget.toLocaleString()}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${Math.min((campaign.spent / campaign.budget) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <Eye size={16} className="text-gray-400 mr-2" />
                          {campaign.impressions.toLocaleString()}
                        </div>
                        <div className="flex items-center">
                          <MousePointer size={16} className="text-gray-400 mr-2" />
                          {campaign.clicks.toLocaleString()}
                        </div>
                        <div className="flex items-center">
                          <ShoppingCart size={16} className="text-gray-400 mr-2" />
                          {campaign.conversions.toLocaleString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => toggleCampaignStatus(campaign.id)}
                          className="text-blue-600 hover:text-blue-800 transition duration-200"
                          title={campaign.status === 'active' ? 'Pause' : 'Resume'}
                        >
                          {campaign.status === 'active' ? <Pause size={16} /> : <Play size={16} />}
                        </button>
                        <button className="text-gray-600 hover:text-gray-800 transition duration-200">
                          <Edit size={16} />
                        </button>
                        <button className="text-red-600 hover:text-red-800 transition duration-200">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Campaign</h3>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter campaign name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter campaign description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Budget</label>
                <input
                  type="number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter budget amount"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};